import { useCallback, useMemo, useSyncExternalStore } from "react";
import de from "./de";
import en from "./en";
import { getLocale, setLocale, subscribe, type Locale } from "./store";
import * as adminDashboardPage from "./modules/adminDashboardPage";
import * as adminGovernance from "./modules/adminGovernance";
import * as adminOps from "./modules/adminOps";
import * as adminTenants from "./modules/adminTenants";
import * as enterprise from "./modules/enterprise";
import * as panels from "./modules/panels";
import * as shared from "./modules/shared";
import * as tenantViews from "./modules/tenantViews";
import * as wizard from "./modules/wizard";

export type { Locale };
export { getLocale, setLocale };
export { applyDocumentLang } from "./store";

/** The order the language switch draws its two options in. */
export const LOCALES: readonly Locale[] = ["en", "de"];

/**
 * Per-view message modules. Each one owns its namespace and carries both
 * languages, so a view can be translated without touching a shared file.
 */
const modules = {
  adminDashboardPage,
  adminGovernance,
  adminOps,
  adminTenants,
  enterprise,
  panels,
  shared,
  tenantViews,
  wizard,
};

/** Fold the modules into one catalog per locale. */
function catalog(locale: Locale): Record<string, unknown> {
  const base: Record<string, unknown> = { ...(locale === "de" ? de : en) };
  for (const [ns, mod] of Object.entries(modules)) {
    base[ns] = mod[locale];
  }
  return base;
}

/** Translation resource map. */
const translations: Record<Locale, Record<string, unknown>> = {
  de: catalog("de"),
  en: catalog("en"),
};

/**
 * Resolve a dot-separated key path against the translation object.
 * Example: resolve("common.actions.save", de) -> "Speichern"
 */
function resolve(key: string, obj: Record<string, unknown>): string {
  const parts = key.split(".");
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== "object") {
      return key; // Key not found, return the key itself.
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current === "string") return current;
  return key; // Leaf is not a string, return key.
}

/** Translate a key against one locale, falling back to German. */
export function translate(locale: Locale, key: string): string {
  const result = resolve(key, translations[locale]);
  if (result === key && locale !== "de") {
    return resolve(key, translations.de);
  }
  return result;
}

/** Subscribe a component to the locale store. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

/**
 * Simple i18n hook.
 *
 * Returns:
 * - `t(key)` -- translate a dot-path key (e.g., "common.actions.save")
 * - `locale` -- current locale
 * - `setLocale(locale)` -- switch locale (persists to localStorage)
 * - `formatDate(date)` -- locale-aware date formatting
 * - `formatNumber(n)` -- locale-aware number formatting
 */
export function useTranslation() {
  const locale = useLocale();

  const t = useCallback((key: string): string => translate(locale, key), [locale]);

  const formatDate = useCallback(
    (date: Date | string): string => {
      const d = typeof date === "string" ? new Date(date) : date;
      return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    },
    [locale],
  );

  const formatNumber = useCallback(
    (n: number): string => new Intl.NumberFormat(locale).format(n),
    [locale],
  );

  return useMemo(
    () => ({ t, locale, setLocale, formatDate, formatNumber }),
    [t, locale, formatDate, formatNumber],
  );
}

export type { TranslationKeys } from "./de";
export { de, en };
